import type { APIPayloadResponse } from '../types/index.js'
import type { AnyObject } from '@policyfly/types/common'

/**
 * Converts the provided value into an array.
 *
 * If not already an array, will create an array with that value as the only item.
 * If already an array, will return the array.
 */
export function castArray<T> (value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value]
}

/**
 * Returns true if passed an array with at least 1 "truthy" item.
 */
export function isTruthyArray<T> (arr: unknown): arr is T[] {
  if (!Array.isArray(arr)) return false
  return arr.some((l) => !!l)
}

/**
 * Returns true if passed an empty array.
 */
export function isEmptyArray (arr: unknown): arr is [] {
  return Array.isArray(arr) && arr.length === 0
}

/**
 * Returns true if the element is within the array.
 * Like `arr.includes()` but allows any element to be compared.
 * See https://github.com/Microsoft/TypeScript/issues/26255
 */
export function arrayIncludes<T> (arr: ReadonlyArray<T>, element: unknown): element is T {
  return arr.includes(element as T)
}

/**
 * Casts a value to a number if it is possible & makes sense (not booleans, arrays etc.).
 * If not will return the original value.
 */
export function castNumber<T> (val: T): number | T {
  if (typeof val !== 'string' && typeof val !== 'number') return val
  const possibleNum = Number(val)
  return isNaN(possibleNum) ? val : possibleNum
}

/**
 * Asserts the passed in value is an object, not including arrays.
 */
export function isObject (val: unknown): val is AnyObject {
  return !!val && typeof val === 'object' && !Array.isArray(val)
}

/**
 * Asserts the passed in value is an {@link APIPayloadResponse}.
 */
export function isAPIPayloadResponse (arg: unknown): arg is APIPayloadResponse {
  if (typeof arg !== 'object' || !arg || Array.isArray(arg)) return false
  return 'v' in arg
}

/**
 * Deep clones an object using `JSON.parse(JSON.stringify(...))`.
 *
 * Note: This will strip out any functions, `undefined` values, and non-JSON-safe values.
 */
export function deepClone <T> (obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}
